import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Helper function to scroll to top
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const Footer = () => {
  const navigate = useNavigate();

  const handleLinkClick = (e, path) => {
    e.preventDefault();
    navigate(path);
    // Small delay to ensure navigation happens first
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  return (
    <div className="relative z-50 bg-white bg-opacity-90 shadow">
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 md:py-16 lg:py-20">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16 lg:gap-20 mb-12">
            {/* Organization Info */}
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold">World March of Women Kenya</h3>
              <p className="text-accent/90 text-sm md:text-base leading-relaxed max-w-sm">
                Empowering women, transforming communities, and building a just society for all Kenyans.
              </p>
              <div className="flex gap-4 pt-2">
                <a 
                  href="https://www.facebook.com/share/1HdcjQTSUX/?mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white transition-colors duration-300"
                  aria-label="Facebook"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/the-world-march-of-women-kenya/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white transition-colors duration-300"
                  aria-label="LinkedIn"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/wmw.kenya?igsh=eHlieHJmODh1aWg2&utm_source=qr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white transition-colors duration-300"
                  aria-label="Instagram"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://x.com/Wmwkenya" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white transition-colors duration-300"
                  aria-label="X (Twitter)"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="space-y-6">
              <h4 className="text-lg md:text-xl font-semibold">Navigation</h4>
              <ul className="space-y-4">
                <li>
                  <Link 
                    to="/about" 
                    onClick={(e) => handleLinkClick(e, '/about')}
                    className="text-accent/90 hover:text-white transition-colors duration-300 text-base"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/blogs" 
                    onClick={(e) => handleLinkClick(e, '/blogs')}
                    className="text-accent/90 hover:text-white transition-colors duration-300 text-base"
                  >
                    Blogs
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/publications" 
                    onClick={(e) => handleLinkClick(e, '/publications')}
                    className="text-accent/90 hover:text-white transition-colors duration-300 text-base"
                  >
                    Publications
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/join" 
                    onClick={(e) => handleLinkClick(e, '/join')}
                    className="text-accent/90 hover:text-white transition-colors duration-300 text-base"
                  >
                    Join Us
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    onClick={(e) => handleLinkClick(e, '/contact')}
                    className="text-accent/90 hover:text-white transition-colors duration-300 text-base"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/mwanzo-mpya" 
                    onClick={(e) => handleLinkClick(e, '/mwanzo-mpya')}
                    className="text-accent/90 hover:text-white transition-colors duration-300 text-base"
                  >
                    Mwanzo Mpya
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <h4 className="text-lg md:text-xl font-semibold">Get in Touch</h4>
              <div className="space-y-5 text-accent/90">
                <div>
                  <p className="text-sm font-medium mb-2 text-white/80">General Email</p>
                  <a 
                    href="mailto:worldmarchofwomenkenya@gmail.com"
                    className="text-sm md:text-base break-words hover:text-white transition-colors duration-300"
                  >
                    worldmarchofwomenkenya@gmail.com
                  </a>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2 text-white/80">Communications</p>
                  <a 
                    href="mailto:communications@worldmarchofwomenkenya.co.ke"
                    className="text-xs sm:text-sm md:text-base hover:text-white transition-colors duration-300 leading-relaxed"
                    style={{ 
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      display: 'block',
                      hyphens: 'auto'
                    }}
                  >
                    communications@worldmarchofwomenkenya.
                    <span style={{ whiteSpace: 'nowrap' }}>co.ke</span>
                  </a>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2 text-white/80">Address</p>
                  <p className="text-sm md:text-base leading-relaxed">
                    P.O. Box 16049-00100<br />
                    Nairobi, Kenya
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm md:text-base text-accent/80 text-center md:text-left">
                &copy; 2025 World March of Women Kenya. All rights reserved.
              </p>
              <Link 
                to="/unsubscribe" 
                onClick={(e) => handleLinkClick(e, '/unsubscribe')}
                className="text-sm text-accent/80 hover:text-white transition-colors duration-300"
              >
                Unsubscribe
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer; 